import { shareOf } from './diskAdminFormat'

import type {
    DiskAdminByState,
    DiskAdminCategory,
    DiskAdminStorageTypeRow,
    DiskAdminTrendPoint,
    DiskUsageCategory,
} from '../Contracts/DiskAdmin'

/**
 * Підготовка даних графіків адмін-панелі (`DISK-006` §5.3) — чиста логіка без
 * React: кольори приходять ззовні (`useChartPalette`, `useOtherColor`), тож
 * її перевіряє юніт-тест, а компоненти лише малюють.
 */

/**
 * Індекс кольору палітри за категорією — колір прив'язаний до категорії, а
 * не до місця в рейтингу: «PDF» на кожному графіку одного кольору. «Інші» —
 * не серія: їм дістається нейтральний колір залишку.
 */
export const CATEGORY_COLOR_INDEX: Record<Exclude<DiskUsageCategory, 'other'>, number> = {
    pdf: 0,
    images: 1,
    documents: 2,
    spreadsheets: 3,
    media: 4,
    archives: 5,
}

/** Сегмент меншої частки не підписується в смузі (але є в легенді). */
export const SEGMENT_LABEL_MIN_SHARE = 2

export interface CategorySegment extends DiskAdminCategory {
    color: string
    /** Частка від суми категорій, 0…100. */
    share: number
    /** Чи підписувати сегмент у самій смузі. */
    labelled: boolean
}

/**
 * Складена смуга «Чим зайнято»: усі сім категорій у порядку беку — у легенді
 * навіть нульові (видно, що відео немає, а не що його забули); у смузі —
 * лише ненульові.
 */
export const categorySegments = (
    rows: readonly DiskAdminCategory[],
    palette: readonly string[],
    otherColor: string
): { segments: CategorySegment[]; total: number } => {
    const total = rows.reduce((sum, row) => sum + Math.max(0, row.bytes), 0)

    const segments = rows.map((row) => {
        const share = shareOf(row.bytes, total)
        const color =
            row.category === 'other'
                ? otherColor
                : (palette[CATEGORY_COLOR_INDEX[row.category] % Math.max(1, palette.length)] ?? otherColor)

        return { ...row, color, share, labelled: share >= SEGMENT_LABEL_MIN_SHARE }
    })

    return { segments, total }
}

/** Назви частин кільця «Стан місця» — і в легенді, і в підказці. */
export const STATE_LABELS: Record<keyof DiskAdminByState, string> = {
    current: 'Поточні версії',
    oldVersions: 'Старі версії',
    trash: 'Кошик',
    reserved: 'Резерв завантажень',
}

/** Індекс кольору палітри для кожної частини стану місця. */
export const STATE_COLOR_INDEX: Record<keyof DiskAdminByState, number> = {
    current: 0,
    oldVersions: 3,
    trash: 4,
    reserved: 2,
}

export interface StateSlice {
    key: keyof DiskAdminByState
    name: string
    value: number
    share: number
    color: string
}

/**
 * Кільце «Стан місця»: поточні / старі версії / кошик / резерв; у центрі —
 * сума, тобто зайнято разом із резервом. Нульові частини в легенді лишаються
 * (з нулем), у кільці — ні.
 */
export const stateSlices = (
    byState: DiskAdminByState,
    palette: readonly string[]
): { slices: StateSlice[]; total: number } => {
    const keys = Object.keys(STATE_LABELS) as Array<keyof DiskAdminByState>
    const total = keys.reduce((sum, key) => sum + Math.max(0, byState[key]), 0)

    return {
        slices: keys.map((key) => ({
            key,
            name: STATE_LABELS[key],
            value: Math.max(0, byState[key]),
            share: shareOf(byState[key], total),
            color: palette[STATE_COLOR_INDEX[key] % Math.max(1, palette.length)] ?? '',
        })),
        total,
    }
}

/** Підписи типів просторів у блоці «За просторами». */
export const STORAGE_GROUP_LABELS: Record<DiskAdminStorageTypeRow['type'], string> = {
    personal: 'Особисті диски',
    company: 'Компанія',
    department: 'Відділи',
    other: 'Інші простори',
}

/** «За просторами»: від більшого до меншого, порожні групи — в кінці, з нулем. */
export const storageGroups = (
    rows: readonly DiskAdminStorageTypeRow[]
): Array<DiskAdminStorageTypeRow & { label: string; share: number }> => {
    const max = Math.max(0, ...rows.map((row) => row.used + row.reserved))

    return [...rows]
        .sort((a, b) => b.used + b.reserved - (a.used + a.reserved))
        .map((row) => ({ ...row, label: STORAGE_GROUP_LABELS[row.type], share: shareOf(row.used + row.reserved, max) }))
}

export interface TrendDatum {
    day: string
    used: number
    files: number
    oldVersions: number
    trash: number
    live: boolean
}

/**
 * Точки тренду за днем. Одна точка (свіжий стенд, знімків ще немає) — не
 * лінія: графік показує її точкою з поясненням, а не порожню вісь.
 */
export const trendData = (points: readonly DiskAdminTrendPoint[]): { data: TrendDatum[]; single: boolean } => {
    const data = [...points]
        .sort((a, b) => a.day.localeCompare(b.day))
        .map((point) => ({
            day: point.day,
            used: Math.max(0, point.used),
            files: point.files,
            oldVersions: point.oldVersions,
            trash: point.trash,
            live: point.live === true,
        }))

    return { data, single: data.length === 1 }
}

/** Зміна за період тренду: остання точка мінус перша; менше двох точок — `null`. */
export const trendDelta = (data: readonly TrendDatum[]): number | null => {
    if (data.length < 2) return null

    return (data[data.length - 1]?.used ?? 0) - (data[0]?.used ?? 0)
}

/**
 * Колір тексту поверх кольорового сегмента: темний на світлому (жовтий,
 * помаранчевий), білий на темному — за відносною яскравістю WCAG. Колір не
 * hex (токен, `var()`) — `null`: підпис у сегменті тоді не малюється, лишається
 * легенда.
 */
export const readableTextOn = (color: string): string | null => {
    const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color.trim())?.[1]

    if (!hex) return null

    const full = hex.length === 3 ? [...hex].map((c) => c + c).join('') : hex
    const channel = (offset: number): number => {
        const value = parseInt(full.slice(offset, offset + 2), 16) / 255

        return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
    }
    const luminance = 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4)

    // Поріг, за якого контраст із чорним і білим однаковий
    return luminance > 0.179 ? 'rgba(0, 0, 0, 0.85)' : '#ffffff'
}
